import { AdjustmentsIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";
import React, { useCallback } from "react";

import WorkflowSelectionSection from "../WorkflowBlock/WorkflowSelectionSection";
import ReturnAllOutputsToggle from "../WorkflowBlock/ReturnAllOutputsToggle";
import VerboseModeToggle from "../WorkflowBlock/VerboseModeToggle";

import { LoopConfiguration } from "./types";

import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { Entity, EntityType } from "@/queries";

interface WorkflowLoopConfigProps {
  workflowName?: string;
  workflowId?: string;
  versionNumber?: number;
  labelName?: string;
  verbose?: boolean;
  returnAllOutputs?: boolean;
  onConfigChange: (config: LoopConfiguration["workflow_config"]) => void;
  readonly?: boolean;
  showVerboseMode?: boolean;
}

const WorkflowLoopConfig = observer(
  ({
    workflowName,
    workflowId,
    versionNumber,
    labelName,
    verbose,
    returnAllOutputs,
    onConfigChange,
    showVerboseMode = true,
  }: WorkflowLoopConfigProps) => {
    const handleWorkflowSelection = useCallback(
      (selection: Entity<EntityType.WORKFLOW>) => {
        if (!selection.id || !selection.name || typeof selection.name !== "string") {
          // eslint-disable-next-line no-console
          console.warn("Invalid workflow selection data:", selection);
          return;
        }

        onConfigChange({
          workflow_id: String(selection.id),
          name: selection.name,
          workflow_label_name: selection.label,
          workflow_version_number: selection.version_number,
          input_variables: {},
          verbose: verbose ?? false,
          return_all_outputs: returnAllOutputs ?? false,
        });
      },
      [onConfigChange, verbose, returnAllOutputs],
    );

    const handleVerboseChange = useCallback(
      (enabled: boolean) => {
        onConfigChange({
          workflow_id: workflowId,
          name: workflowName || "",
          workflow_version_number: versionNumber,
          workflow_label_name: labelName,
          input_variables: {},
          verbose: enabled,
          return_all_outputs: returnAllOutputs ?? false,
        });
      },
      [onConfigChange, workflowId, workflowName, versionNumber, labelName, returnAllOutputs],
    );

    const handleReturnAllOutputsChange = useCallback(
      (enabled: boolean) => {
        onConfigChange({
          workflow_id: workflowId,
          name: workflowName || "",
          workflow_version_number: versionNumber,
          workflow_label_name: labelName,
          input_variables: {},
          verbose: verbose ?? false,
          return_all_outputs: enabled,
        });
      },
      [onConfigChange, workflowId, workflowName, versionNumber, labelName, verbose],
    );

    return (
      <div className="space-y-4">
        <WorkflowSelectionSection
          workflowName={workflowName}
          workflowId={workflowId}
          workflowVersionNumber={versionNumber}
          workflowLabelName={labelName}
          onWorkflowSelection={handleWorkflowSelection}
        />

        {showVerboseMode && (
          <SectionGroup
            title="Advanced Settings"
            variant="compact"
            collapsible
            defaultExpanded={false}
            icon={<AdjustmentsIcon className="h-4 w-4" />}
          >
            <div className="space-y-4">
              <VerboseModeToggle
                enabled={verbose || false}
                onChange={handleVerboseChange}
                showVerboseMode={showVerboseMode}
                isLoading={false}
              />
              <ReturnAllOutputsToggle
                enabled={returnAllOutputs || false}
                onChange={handleReturnAllOutputsChange}
                isLoading={false}
              />
            </div>
          </SectionGroup>
        )}
      </div>
    );
  },
);

export default WorkflowLoopConfig;
